import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  HeadingText,
  ImageMessage,
  Modal,
  ParagraphText,
  StackLayout,
  VisuallyHidden,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import { useNavigate } from 'react-router-dom';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { useDeleteAccountMutation } from 'hooks/use-delete-account-mutation.hook';
import leaveOrExitWarning from 'assets/leave-or-exit-warning.svg';
import windingRoadIllustration from 'assets/winding-road-illustration.svg';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AccountDeletionModal = ({ isOpen, onClose }: ModalProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  const [isConfirmationStep, setIsConfirmationStep] = useState(false);
  const {
    mutate: deleteAccount,
    isLoading: isDeletingAccount,
    isError: didDeletionFail,
    reset: resetDeletion,
  } = useDeleteAccountMutation();

  useEffect(() => {
    if (isOpen) {
      setIsConfirmationStep(false);
      resetDeletion();
      trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
        product_area: PRODUCT_AREA.SETTINGS,
        screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
      });
    }
  }, [isOpen, resetDeletion]);

  const handleDeleteAccount = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
      detail: 'confirm deactivation',
    });
    deleteAccount(undefined, {
      onSuccess: () => setIsConfirmationStep(true),
    });
  };

  const handleCancelDeletion = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
      detail: 'cancel deactivation',
    });
  };

  const handleAcknowledgeDeletion = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
      detail: 'acknowledge deactivation',
    });
  };

  const handleCloseModal = (open: boolean) => {
    // Radix fires onOpenChange for both open and close; only react to close.
    if (open) return;
    onClose();
    // The confirmation step is only reached after the account is already
    // deleted, so signing out on ANY dismissal (X, Escape, backdrop, or the
    // "I understand" button) is intentional — the session is no longer valid.
    if (isConfirmationStep) {
      navigate(APP_MANAGED_ROUTES.SIGN_OUT);
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleCloseModal}>
      <Modal.Content
        showCloseButton={false}
        padding="$none"
        width={{
          '@initial': 580,
          '@mobile': '100%',
          '@mobileLandscape': '100%',
        }}
      >
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: 44,
            paddingInline: '$half',
          }}
        >
          <Modal.Title>
            {isConfirmationStep ? (
              <VisuallyHidden>
                {formatMessage({ id: 'STR_PROFILE_DEACTIVATED_HEADING' })}
              </VisuallyHidden>
            ) : (
              <Box
                as="span"
                css={{
                  typography: '$subtitleOne',
                  color: '$onSurfaceTextPrimary',
                }}
              >
                {formatMessage({ id: 'STR_DEACTIVATE_PORTAL_PROFILE' })}
              </Box>
            )}
          </Modal.Title>
          {!isConfirmationStep && (
            <Modal.Close>
              <Button
                priority="tertiary"
                quiet
                icon="tinyClose"
                hideLabel
                size="small"
                css={{
                  position: 'absolute',
                  right: '$half',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '$onSurfaceIconPrimary',
                }}
              >
                {formatMessage({ id: 'STR_CLOSE' })}
              </Button>
            </Modal.Close>
          )}
        </Box>
        <Box css={{ padding: '$oneAndHalf' }}>
          {!isConfirmationStep ? (
            <ImageMessage
              image={leaveOrExitWarning}
              imageGap="$oneAndHalf"
              imageWidth={176}
            >
              <StackLayout horizontalAlignment="stretch">
                <HeadingText
                  size="lg"
                  level="3"
                  css={{ marginBlockEnd: '$one' }}
                >
                  {formatMessage({ id: 'STR_ACCOUNT_DEACTIVATION_HEADING' })}
                </HeadingText>
                {/*
                  asChild renders the description as a Box (div) instead of the
                  default <p>, so the bulleted lists and paragraphs below are
                  valid block content and stay associated via aria-describedby.
                */}
                <Modal.Description asChild>
                  <Box>
                    <ParagraphText css={{ marginBlockEnd: '$half !important' }}>
                      {formatMessage({
                        id: 'STR_ACCOUNT_DEACTIVATION_WILL_INTRO',
                      })}
                    </ParagraphText>
                    <Box
                      as="ul"
                      css={{
                        listStyleType: 'disc',
                        paddingInlineStart: '$oneAndHalf',
                        marginBlockStart: '$none',
                        marginBlockEnd: '$half',
                      }}
                    >
                      <Box as="li">
                        <ParagraphText>
                          {formatMessage({
                            id: 'STR_ACCOUNT_DEACTIVATION_WILL_1',
                          })}
                        </ParagraphText>
                      </Box>
                      <Box as="li">
                        <ParagraphText>
                          {formatMessage({
                            id: 'STR_ACCOUNT_DEACTIVATION_WILL_2',
                          })}
                        </ParagraphText>
                      </Box>
                    </Box>
                    <ParagraphText css={{ marginBlockEnd: '$half !important' }}>
                      {formatMessage({
                        id: 'STR_ACCOUNT_DEACTIVATION_WILL_NOT_INTRO',
                      })}
                    </ParagraphText>
                    <Box
                      as="ul"
                      css={{
                        listStyleType: 'disc',
                        paddingInlineStart: '$oneAndHalf',
                        marginBlockStart: '$none',
                        marginBlockEnd: '$half',
                      }}
                    >
                      <Box as="li">
                        <ParagraphText>
                          {formatMessage({
                            id: 'STR_ACCOUNT_DEACTIVATION_WILL_NOT_1',
                          })}
                        </ParagraphText>
                      </Box>
                      <Box as="li">
                        <ParagraphText>
                          {formatMessage({
                            id: 'STR_ACCOUNT_DEACTIVATION_WILL_NOT_2',
                          })}
                        </ParagraphText>
                      </Box>
                      <Box as="li">
                        <ParagraphText>
                          {formatMessage({
                            id: 'STR_ACCOUNT_DEACTIVATION_WILL_NOT_3',
                          })}
                        </ParagraphText>
                      </Box>
                    </Box>
                    <ParagraphText>
                      {formatMessage({
                        id: 'STR_ACCOUNT_DEACTIVATION_FOOTER',
                      })}
                    </ParagraphText>
                  </Box>
                </Modal.Description>

                {didDeletionFail && (
                  <ParagraphText
                    role="alert"
                    css={{
                      color: '$onSurfaceTextCritical',
                      marginBlockStart: '$one',
                    }}
                  >
                    {formatMessage({ id: 'STR_ACCOUNT_DEACTIVATION_ERROR' })}
                  </ParagraphText>
                )}

                <StackLayout
                  horizontalAlignment={isBelowTablet ? 'stretch' : 'end'}
                >
                  <Box
                    css={{
                      display: 'flex',
                      flexDirection: isBelowTablet ? 'column-reverse' : 'row',
                      gap: '$one',
                      marginBlockStart: '$twoAndHalf',
                    }}
                  >
                    <Modal.Close>
                      <Button
                        priority="secondary"
                        onClick={handleCancelDeletion}
                        width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                      >
                        {formatMessage({ id: 'STR_NEVERMIND' })}
                      </Button>
                    </Modal.Close>
                    <Button
                      onClick={handleDeleteAccount}
                      disabled={isDeletingAccount}
                      loading={isDeletingAccount}
                      destructive
                      width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                    >
                      {formatMessage({ id: 'STR_YES_DEACTIVATE_PROFILE' })}
                    </Button>
                  </Box>
                </StackLayout>
              </StackLayout>
            </ImageMessage>
          ) : (
            <ImageMessage
              image={windingRoadIllustration}
              imageGap="$oneAndHalf"
              imageWidth={176}
            >
              {/* role="status" announces the success step politely when it
                  swaps in — the button the user clicked unmounts, so without
                  it the step change is silent to assistive tech. */}
              <StackLayout role="status" horizontalAlignment="stretch">
                <HeadingText
                  size="lg"
                  level="3"
                  css={{ marginBlockEnd: '$one' }}
                >
                  {formatMessage({ id: 'STR_PROFILE_DEACTIVATED_HEADING' })}
                </HeadingText>
                <Modal.Description asChild>
                  <Box>
                    <ParagraphText css={{ marginBlockEnd: '$half !important' }}>
                      {formatMessage({ id: 'STR_PROFILE_DEACTIVATED_BODY_1' })}
                    </ParagraphText>
                    <ParagraphText>
                      {formatMessage({ id: 'STR_PROFILE_DEACTIVATED_BODY_2' })}
                    </ParagraphText>
                  </Box>
                </Modal.Description>

                <StackLayout
                  horizontalAlignment={isBelowTablet ? 'center' : 'end'}
                >
                  <Modal.Close>
                    <Button
                      priority="primary"
                      onClick={handleAcknowledgeDeletion}
                      css={{ marginBlockStart: '$twoAndHalf' }}
                      width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                    >
                      {formatMessage({ id: 'STR_I_UNDERSTAND' })}
                    </Button>
                  </Modal.Close>
                </StackLayout>
              </StackLayout>
            </ImageMessage>
          )}
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
};
